import React from 'react';
import {
  Datagrid,
  List,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  useDataProvider,
  useListContext
} from 'react-admin';
import { Breadcrumbs, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useEffect, useState } from 'react';
import { ContactBulkActions, ContactListActions } from './components/ContactExportActions.tsx';
import { RecordRowActions } from '../components/record/RecordRowActions.tsx';
import { ImportEmpty } from '../data-import/ImportButton.tsx';

const ContactListGrid: React.FC = () => {
  const { filterValues, sort } = useListContext();
  const dataProvider = useDataProvider();
  const [contactIds, setContactIds] = useState<number[]>([]);
  const hasFilters =
    Boolean(filterValues?.q) ||
    typeof filterValues?.contactStatusId === 'number' ||
    typeof filterValues?.industryId === 'number';

  useEffect(() => {
    if (!hasFilters) {
      setContactIds([]);
      return;
    }
    dataProvider
      .getList('contacts', {
        pagination: { page: 1, perPage: 10000 },
        sort: sort ?? { field: 'lastName', order: 'ASC' },
        filter: filterValues ?? {}
      })
      .then(({ data }) => setContactIds(data.map((record) => record.id)))
      .catch(() => setContactIds([]));
  }, [dataProvider, filterValues, hasFilters, sort]);

  return (
    <Datagrid rowClick="show" bulkActionButtons={<ContactBulkActions />}>
      <TextField source="id" />
      <TextField source="firstName" />
      <TextField source="lastName" />
      <TextField source="contactStatus.name" label="Status" />
      <TextField source="company.name" label="Company" />
      <TextField source="industry.name" label="Industry" />
      <RecordRowActions editState={{ contactIds, hasFilters }} />
    </Datagrid>
  );
};

const ContactList: React.FC = () => {
  const ContactFilters = [
    <TextInput key="search" label="Search" source="q" alwaysOn />,
    <ReferenceInput
      key="status"
      label="Status"
      source="contactStatusId"
      reference="contact-statuses"
      alwaysOn
    >
      <SelectInput optionText="name" emptyText="All" />
    </ReferenceInput>,
    <ReferenceInput
      key="industry"
      label="Industry"
      source="industryId"
      reference="industry"
      alwaysOn
    >
      <SelectInput optionText="name" emptyText="All" />
    </ReferenceInput>
  ];

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Contacts</Typography>
      </Breadcrumbs>
      <List
        filters={ContactFilters}
        actions={<ContactListActions />}
        empty={<ImportEmpty kind="contacts" />}
      >
        <ContactListGrid />
      </List>
    </Container>
  );
};

export default ContactList;
